/* eslint-disable no-unused-vars */
/**
 * React hook for managing show idea generation state
 *
 * Uses the queue-centric architecture:
 * 1. Call generateShowIdeas() → get jobId
 * 2. Use useQueueJob to poll for completion
 * 3. When job completes, extract ideas from output
 *
 * @fileoverview Custom hook for show idea generation workflow
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { ShowIdea } from '@musedrops/shared';
import { logger } from '@musedrops/shared';
import { generateShowIdeas } from '../services/showIdeaService';
import { useQueueJob } from './useQueueJob';

interface GenerateIdeasParams {
  genreId: string;
  genreName: string;
  genreDescription: string;
  ideasCount: number;
}

// Toast notification state
interface ToastState {
  open: boolean;
  message: string;
  severity?: 'error' | 'warning' | 'info' | 'success';
}

// Hook state for managing show idea generation
interface UseShowIdeasState {
  loading: boolean;
  ideas: ShowIdea[];
  error: string | null;
  toast: ToastState;
  jobId: number | null;
  generateIdeas: (params: GenerateIdeasParams) => Promise<void>;
  clearError: () => void;
  closeToast: () => void;
  showToast: (message: string, severity?: ToastState['severity']) => void;
}

/**
 * Custom hook for managing show idea generation.
 *
 * Uses queue-centric architecture with real-time polling:
 * - Creates queue job via edge function
 * - Polls for job completion using Supabase real-time
 * - Extracts ideas from job output when complete
 *
 * @returns {UseShowIdeasState} State object with ideas, loading status, and control functions
 *
 * @example
 * ```tsx
 * const { ideas, loading, generateIdeas, error } = useShowIdeas();
 *
 * const handleGenerate = async () => {
 *   await generateIdeas({ genreId, genreName, genreDescription, ideasCount: 5 });
 *   // ideas will be populated when job completes
 * };
 * ```
 */
export const useShowIdeas = (): UseShowIdeasState => {
  const [loading, setLoading] = useState(false);
  const [ideas, setIdeas] = useState<ShowIdea[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState>({ open: false, message: '' });
  const [jobId, setJobId] = useState<number | null>(null);

  // Track if we're expecting a job completion
  const expectingJobRef = useRef(false);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const closeToast = useCallback(() => {
    setToast({ open: false, message: '' });
  }, []);

  const showToast = useCallback(
    (message: string, severity: ToastState['severity'] = 'error') => {
      setToast({ open: true, message, severity });
    },
    []
  );

  // Handle job completion
  const handleJobComplete = useCallback(
    (output: Record<string, unknown>) => {
      logger.info('Show ideas job completed', { output });

      // Extract shows array from output
      const shows = output?.shows as ShowIdea[] | undefined;

      if (shows && Array.isArray(shows) && shows.length > 0) {
        setIdeas(shows);
        showToast(`Generated ${shows.length} show ideas`, 'success');
      } else {
        setIdeas([]);
        showToast('No ideas were generated. Please try again.', 'warning');
      }

      setLoading(false);
      expectingJobRef.current = false;
    },
    [showToast]
  );

  // Handle job error
  const handleJobError = useCallback(
    (errorMsg: string) => {
      logger.error('Show ideas job failed', { error: errorMsg });
      setError(errorMsg);
      showToast(errorMsg, 'error');
      setIdeas([]);
      setLoading(false);
      expectingJobRef.current = false;
    },
    [showToast]
  );

  // Handle job timeout
  const handleJobTimeout = useCallback(() => {
    const msg = 'Show ideas generation timed out. Please try again.';
    setError(msg);
    showToast(msg, 'error');
    setLoading(false);
    expectingJobRef.current = false;
  }, [showToast]);

  // Use queue job hook for polling
  const { watchJob, status, stopWatching } = useQueueJob({
    timeout: 3 * 60 * 1000, // 3 minutes for show ideas
    onComplete: handleJobComplete,
    onError: handleJobError,
    onTimeout: handleJobTimeout,
  });

  // Update loading state based on job status
  useEffect(() => {
    if (status && expectingJobRef.current) {
      if (status.status === 'processing') {
        // Still processing, ensure loading is true
        setLoading(true);
      }
    }
  }, [status]);

  const generateIdeas = useCallback(
    async (params: GenerateIdeasParams) => {
      if (loading) {
        return; // Prevent multiple simultaneous requests
      }

      if (!params.genreId) {
        setError('Please select a genre before generating ideas.');
        showToast('Please select a genre before generating ideas.', 'warning');
        return;
      }

      setLoading(true);
      setError(null);
      setIdeas([]);
      closeToast();
      expectingJobRef.current = true;

      try {
        // Create queue job
        const response = await generateShowIdeas(params);

        if (response.success && response.result?.jobId) {
          const newJobId = response.result.jobId;
          setJobId(newJobId);

          logger.info('Show ideas job created, watching for completion', {
            jobId: newJobId,
          });

          // Start watching the job for completion
          watchJob(newJobId);
        } else {
          const errorMessage =
            response.error || 'Failed to create generation job';
          setError(errorMessage);
          showToast(errorMessage, 'error');
          setLoading(false);
          expectingJobRef.current = false;
        }
      } catch (err) {
        logger.error('Generate ideas hook error', { error: err });
        const errorMessage =
          err instanceof Error ? err.message : 'An unexpected error occurred';
        const fullErrorMessage = `Error generating ideas: ${errorMessage}`;

        setError(fullErrorMessage);
        showToast(fullErrorMessage, 'error');
        setLoading(false);
        expectingJobRef.current = false;
      }
    },
    [loading, closeToast, showToast, watchJob]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopWatching();
    };
  }, [stopWatching]);

  return {
    loading,
    ideas,
    error,
    toast,
    jobId,
    generateIdeas,
    clearError,
    closeToast,
    showToast,
  };
};
